import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ScanProvider } from "./contexts/ScanContext";
import { ComplianceProvider } from "./contexts/ComplianceContext";
import { PentestProvider } from "./contexts/PentestContext";
import WelcomePage from "./pages/WelcomePage";
import ScanPage from "./pages/ScanPage";
import ResultsPage from "./pages/ResultsPage";
import FindingDetailPage from "./pages/FindingDetailPage";
import LoginPage from "./pages/LoginPage";
import ComplianceRedirect from "./pages/compliance/ComplianceRedirect";
import ComplianceOnboardingWizard from "./pages/compliance/ComplianceOnboardingWizard";
import ComplianceProjectDetail from "./pages/compliance/ComplianceProjectDetail";
import CompliancePoliciesPage from "./pages/compliance/CompliancePoliciesPage";
import PolicyEditor from "./pages/compliance/PolicyEditor";
import ComplianceProjectsList from "./pages/compliance/ComplianceProjectsList";
import IntegrationsPage from "./pages/compliance/IntegrationsPage";
import ExportPage from "./pages/compliance/ExportPage";
import RiskAssessmentPage from "./pages/compliance/RiskAssessmentPage";
import TrustCenterPage from "./pages/compliance/TrustCenterPage";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import TrustCenterPublicPage from "./pages/TrustCenterPublicPage";
import Repositories from "./components/repositories/Repositories";
import RepositoryDetail from "./components/repositories/RepositoryDetail";
import ScansPage from "./pages/ScansPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import StartPentestWizard from "./pages/StartPentestWizard";
import PentestResultsPage from "./pages/PentestResultsPage";
import "./index.css";
import ProtectedRoute from "./components/ProtectedRoute";
import GuestAllowedRoute from "./components/GuestAllowedRoute";
import { useScan } from "./contexts/ScanContext";
import { GUEST_SCANS_ENABLED, takeReturnPath } from "./utils/guestScans";
import ExternalRedirect from "./components/ExternalRedirect";
import SidebarLayout from "./components/SidebarLayout";

// Signed-in users land on their repositories; visitors (when guest scans are on) on the
// public repository scan form instead of the login page.
const HomeRedirect = () => {
  const { user, authChecking } = useScan();

  if (!GUEST_SCANS_ENABLED) {
    return (
      <ProtectedRoute>
        <Navigate to="/repositories" replace />
      </ProtectedRoute>
    );
  }
  if (authChecking) {
    return <div>Loading...</div>;
  }
  return <Navigate to={user ? "/repositories" : "/scan?method=direct"} replace />;
};

// After a guest signs in from a scan's results, go back to that scan (claimed by then, so
// with every action) instead of the default landing page. Rendered after <Routes> so it
// wins over a landing page's own redirect in the same render.
const PostLoginReturn = () => {
  const { user, authChecking } = useScan();
  const navigate = useNavigate();

  useEffect(() => {
    if (authChecking || !user) return;
    const path = takeReturnPath();
    if (path) navigate(path, { replace: true });
  }, [user, authChecking, navigate]);

  return null;
};

const App = () => {
  return (
    <ScanProvider>
      <ComplianceProvider>
      <PentestProvider>
      <div className="min-h-screen w-full h-full">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Legal pages live on the marketing site so there is a single copy. */}
          <Route
            path="/privacy-policy"
            element={<ExternalRedirect to="https://orbisappsec.com/privacy-policy" />}
          />
          <Route
            path="/terms-of-service"
            element={<ExternalRedirect to="https://orbisappsec.com/terms-of-service" />}
          />

          {/* Default route */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Routes WITHOUT sidebar, kept for legacy or specific access */}
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <WelcomePage />
              </ProtectedRoute>
            }
          />
          {/* Scanning a public repository and viewing its results also work without a login */}
          <Route
            path="/scan"
            element={
              <GuestAllowedRoute>
                <ScanPage />
              </GuestAllowedRoute>
            }
          />
          <Route
            path="/results/:scanId"
            element={
              <GuestAllowedRoute>
                <ResultsPage />
              </GuestAllowedRoute>
            }
          />
          <Route
            path="/findings/:scanId/:vulnId"
            element={
              <ProtectedRoute>
                <FindingDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Compliance: smart redirect to first project or onboarding */}
          <Route
            path="/compliance"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ComplianceRedirect />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Onboarding Wizard */}
          <Route
            path="/compliance/new"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ComplianceOnboardingWizard />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Projects List */}
          <Route
            path="/compliance/projects"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ComplianceProjectsList />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Policy Editor */}
          <Route
            path="/compliance/projects/:projectId/policies/:policyId"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <PolicyEditor />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Policies List */}
          <Route
            path="/compliance/projects/:projectId/policies"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <CompliancePoliciesPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Integrations */}
          <Route
            path="/compliance/projects/:projectId/integrations"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <IntegrationsPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Risk Assessment */}
          <Route
            path="/compliance/projects/:projectId/risk-assessment"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <RiskAssessmentPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Export */}
          <Route
            path="/compliance/projects/:projectId/export"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ExportPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Trust Center Admin */}
          <Route
            path="/compliance/projects/:projectId/trust-center"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <TrustCenterPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Compliance Project Detail (Dashboard) */}
          <Route
            path="/compliance/projects/:projectId"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ComplianceProjectDetail />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />

          {/* Routes WITH sidebar */}
          {/* /dashboard is in the sidebar's own nav list, so rendering it
              without the shell made the nav disappear with no way back. */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <DashboardPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/repositories"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <Repositories />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/repositories/:owner/:repo"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <RepositoryDetail />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scans"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ScansPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          {/* Applications / Pentest */}
          <Route
            path="/applications"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ApplicationsPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/:appId/pentest/new"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <StartPentestWizard />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/:appId"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <ApplicationDetailPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pentest/:pentestId/results"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <PentestResultsPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SidebarLayout>
                  <SettingsPage />
                </SidebarLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog"
            element={
              <BlogPage />
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <BlogPostPage />
            }
          />
          <Route path="/trust/:slug" element={<TrustCenterPublicPage />} />
        </Routes>
        <PostLoginReturn />
      </div>
      </PentestProvider>
      </ComplianceProvider>
    </ScanProvider>
  );
};

export default App;
